import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import LeavesCalendarGrid, { ILeaveData } from 'components/leavescalendargrid';

interface IProps {
  leavesData: ILeaveData[];
  holidaysData: any[];
  date: Date;
  onDateChange?: (newDate: Date) => void;
  isLoading?: boolean;
}

const ColleagueLeavesGrid: FC<IProps> = ({ leavesData, holidaysData, date, onDateChange, isLoading }) => {
  const { t } = useTranslation();

  const getLeaveColorForDay = (leaves: ILeaveData[], day: number, currentDate: Date): string | null => {
    const leave = getLeaveForDay(leaves, day, currentDate);
    if (!leave) return null;

    const isPending =
      leave.status === 'pending' ||
      leave.status === 'signing' ||
      leave.status === 'approved' ||
      leave.status === 'initiated' ||
      leave.status === 'inprogress';

    if (leave.sub_type === 'work_from_home') {
      return isPending ? 'rgba(255, 165, 90, 0.2)' : 'rgba(255, 165, 90, 1)';
    }

    if (leave.sub_type === 'work_from_office') {
      return isPending ? 'rgba(184, 86, 4, 0.2)' : 'rgba(184, 86, 4, 1)';
    }

    return isPending ? 'rgba(50, 191, 163, 0.2)' : 'rgba(50, 191, 163, 1)';
  };

  const getLeaveForDay = (leaves: ILeaveData[], day: number, currentDate: Date): ILeaveData | null => {
    const checkDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    checkDate.setHours(0, 0, 0, 0);

    const leave = leaves.find(l => {
      const leaveStart = new Date(l.date_from);
      leaveStart.setHours(0, 0, 0, 0);

      const leaveEnd = new Date(l.date_to);
      leaveEnd.setHours(0, 0, 0, 0);

      return checkDate >= leaveStart && checkDate <= leaveEnd;
    });

    return leave || null;
  };

  const getLeaveTooltipContent = (leave: ILeaveData): JSX.Element => {
    const isPending =
      leave.status === 'pending' ||
      leave.status === 'signing' ||
      leave.status === 'approved' ||
      leave.status === 'initiated' ||
      leave.status === 'inprogress';

    const statusText = isPending ? t('leaves.legend.waitingForConfirmation') : t('leaves.legend.confirmed');
    const commentText = leave.comment ? `${t('leaves.comment')}: ${leave.comment}` : t('leaves.noComment');

    return (
      <span>
        {statusText}
        <br />
        {commentText}
      </span>
    );
  };

  return (
    <LeavesCalendarGrid
      leavesData={leavesData}
      holidaysData={holidaysData}
      date={date}
      onDateChange={onDateChange}
      getLeaveColorForDay={getLeaveColorForDay}
      getLeaveTooltipContent={getLeaveTooltipContent}
      isLoading={isLoading}
    />
  );
};

export default ColleagueLeavesGrid;
